import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Icon } from '@/components/synapse/Icon';
import { StatusDot } from '@/components/synapse/StatusDot';
import { ApiError } from '@/lib/api';
import { translateApiError } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import {
  GESTATION_DAYS,
  eddFromLmp,
  isConcludable,
  poaFromLmp,
  trimester,
} from '@/lib/gestogram';
import { isValidIsoDate } from '@/lib/calendar';
import type { ChronicDisease, ClinicalHistory, Consultation } from '@/types/patients';
import { usePatient } from './usePatients';
import { usePatientHistory } from './usePatientHistory';
import { usePatientConsultations } from './useConsultations';
import { SummaryCard, SummaryCardRow } from '@/components/synapse/SummaryCard';
import { SummaryFlagsRow } from './SummaryFlagsRow';
import { VisitTimeline } from './VisitTimeline';
import { PatientLabsCard } from '@/features/lab/PatientLabsCard';

interface PatientSummaryPanelProps {
  patientId: string;
  /** Compact layout for the desktop master–detail pane. */
  narrow?: boolean;
}

/**
 * The patient's longitudinal chart, composed from three sources (spec §4):
 *   - patient detail   → demographics + nationalId/address/bloodGroup
 *   - clinical history → allergies / chronic / past medical+surgical / family+social / obstetric
 *   - consultations    → visit timeline + current medications (latest consult)
 *
 * Chronic diseases ← history.chronicDiseases; Current medications ← the latest
 * consultation's currentMedications; timeline ← the consultations list.
 */
export function PatientSummaryPanel({ patientId, narrow }: PatientSummaryPanelProps) {
  const { t } = useTranslation();
  const patientQ = usePatient(patientId);
  const historyQ = usePatientHistory(patientId);
  const consultsQ = usePatientConsultations(patientId);

  if (patientQ.isLoading) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true">
        <div className="h-16 animate-pulse rounded-lg bg-paper-2" />
        <div className="h-40 animate-pulse rounded-lg bg-paper-2" />
        <div className="h-40 animate-pulse rounded-lg bg-paper-2" />
      </div>
    );
  }
  if (patientQ.isError || !patientQ.data) {
    const msg =
      patientQ.error instanceof ApiError ? translateApiError(patientQ.error) : t('patients.couldNotLoadMsg');
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive-bg text-destructive">
          <Icon name="alert" size={22} />
        </div>
        <div className="text-[14px] font-semibold tracking-[-0.01em]">{t('patients.couldNotLoad')}</div>
        <p className="max-w-[320px] text-[12.5px] text-muted">{msg}</p>
      </div>
    );
  }

  const patient = patientQ.data;
  const history = historyQ.data;
  const consults = consultsQ.data ?? [];
  // Latest consultation by visit date — source for "current medications".
  const latest = [...consults].sort((a, b) => b.visitDate.getTime() - a.visitDate.getTime())[0];

  const allergiesCard = (
    <SummaryCard titleKey="patients.cardAllergies" icon="alert" danger>
      <SummaryCardRow first>
        {history && history.payload.allergies.length > 0 ? (
          <div className="flex flex-col gap-2">
            {history.payload.allergies.map((a, i) => (
              <div key={`${a.substance}-${i}`} className="flex items-center gap-2.5">
                <span className="text-[14px] font-bold text-destructive">{a.substance}</span>
                {a.reaction && (
                  <Badge tone="danger" size="sm">
                    {a.reaction}
                  </Badge>
                )}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-[12.5px] text-muted">{t('patients.noKnownAllergies')}</span>
        )}
        {historyQ.isError && (
          <span className="mt-1 block text-[11.5px] text-destructive">{t('patients.couldNotLoadHistory')}</span>
        )}
      </SummaryCardRow>
    </SummaryCard>
  );

  const chronic = history?.payload.chronicDiseases ?? [];
  const chronicCard = (
    <SummaryCard titleKey="patients.cardChronicDiseases" icon="info">
      {chronic.length > 0 ? (
        chronic.map((c, i) => {
          // Meta line: "Since <year> · on <drug, drug>" — each clause dropped
          // when the underlying field is absent, so a bare condition shows no
          // empty separators.
          const meta = chronicMeta(c, t);
          return (
            <SummaryCardRow key={`${c.condition}-${i}`} first={i === 0}>
              <div className="flex items-center gap-2">
                <span className="text-[13.5px] font-semibold tracking-[-0.01em]">{c.condition}</span>
                <StatusDot tone="neutral" />
              </div>
              {meta && <div className="mt-0.5 text-[12px] text-muted">{meta}</div>}
            </SummaryCardRow>
          );
        })
      ) : (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-muted">{t('patients.noChronicDiseases')}</span>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );

  const meds = latest?.payload.currentMedications ?? [];
  const medsCard = (
    <SummaryCard titleKey="patients.cardCurrentMeds" icon="pill">
      {meds.length > 0 ? (
        meds.map((m, i) => (
          <SummaryCardRow key={`${m.drug.label}-${i}`} first={i === 0}>
            <div className="flex items-center gap-2.5">
              <span className="text-[13.5px] font-semibold">{m.drug.label}</span>
              {(m.dose || m.frequency) && (
                <span className="font-mono text-[12.5px] text-muted">
                  {[m.dose, m.frequency].filter(Boolean).join(' · ')}
                </span>
              )}
              <span className="ml-auto">
                <Badge tone="medication" size="sm">
                  {t('itemType.medication')}
                </Badge>
              </span>
            </div>
          </SummaryCardRow>
        ))
      ) : (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-muted">{t('patients.noCurrentMeds')}</span>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );

  const pastMedical = history?.payload.pastMedical ?? [];
  const pastSurgical = history?.payload.pastSurgical ?? [];
  const hasPast = pastMedical.length > 0 || pastSurgical.length > 0;
  const pastCard = (
    <SummaryCard titleKey="patients.cardPastMedical" icon="calendar">
      {hasPast ? (
        <>
          {pastMedical.map((m, i) => (
            <SummaryCardRow key={`pm-${i}`} first={i === 0}>
              <span className="text-[13.5px] font-semibold">{m}</span>
            </SummaryCardRow>
          ))}
          {pastSurgical.map((s, i) => (
            <SummaryCardRow key={`ps-${i}`} first={pastMedical.length === 0 && i === 0}>
              <div className="flex items-center gap-2">
                <span className="text-[13.5px] font-semibold">{s.procedure}</span>
                {s.year != null && (
                  <span className="ml-auto font-mono text-[12px] text-muted">{s.year}</span>
                )}
              </div>
            </SummaryCardRow>
          ))}
        </>
      ) : (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-muted">{t('patients.noPastHistory')}</span>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );

  // Obstetric snapshot + pregnancy flag: only for female patients, and only when
  // we can derive a POA from an LMP recorded on a consultation's gynae snapshot.
  const lmp = findLmp(consults, history);
  const isPregnant = patient.sex === 'female' && !!lmp;
  const pregnancyPoa = isPregnant
    ? (() => {
        const poa = poaFromLmp(lmp!);
        return t('patients.obWeeksDays', { weeks: poa.weeks, days: poa.days });
      })()
    : undefined;
  const gravidaPara = formatGravidaPara(history?.payload.obstetric?.parity);
  const obstetricCard = isPregnant ? (
    <ObstetricSnapshotCard lmpIso={lmp!} gravidaPara={gravidaPara} />
  ) : null;

  const family = history?.payload.family ?? [];
  const social = history?.payload.social;
  const hasFamilySocial =
    family.length > 0 || !!(social && (social.smoking || social.alcohol || social.occupationNotes));
  const familyCard = (
    <SummaryCard titleKey="patients.cardFamilySocial" icon="users">
      {hasFamilySocial ? (
        <>
          {family.length > 0 && (
            <SummaryCardRow first>
              <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
                {t('patients.familyLabel')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {family.map((f, i) => (
                  <Badge key={`${f}-${i}`} tone="neutral" size="md">
                    {f}
                  </Badge>
                ))}
              </div>
            </SummaryCardRow>
          )}
          {social && (social.smoking || social.alcohol || social.occupationNotes) && (
            <SummaryCardRow first={family.length === 0}>
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-2">
                {t('patients.socialLabel')}
              </div>
              {social.occupationNotes && (
                <div className="text-[13px] text-ink-2">{social.occupationNotes}</div>
              )}
              {(social.smoking || social.alcohol) && (
                <div className="mt-0.5 text-[12.5px] text-muted">
                  {[social.smoking, social.alcohol].filter(Boolean).join(' · ')}
                </div>
              )}
            </SummaryCardRow>
          )}
        </>
      ) : (
        <SummaryCardRow first>
          <span className="text-[12.5px] text-muted">{t('patients.noFamilySocial')}</span>
        </SummaryCardRow>
      )}
    </SummaryCard>
  );

  const timeline = <VisitTimeline patientId={patientId} />;

  // Objective evidence belongs next to the narrative it was ordered against.
  // Renders nothing without `lab.read`, so it simply does not appear for a
  // member who has no business seeing it.
  const labsCard = <PatientLabsCard patientId={patientId} />;

  // Loud at-a-glance safety band directly under the header: allergy/blood/
  // chronic/pregnant pills (matches the design's SummaryFlags row).
  const flagsRow = (
    <SummaryFlagsRow
      history={history}
      bloodGroup={patient.bloodGroup}
      pregnancyPoa={pregnancyPoa}
    />
  );

  // Desktop wide: 1.5fr / 1fr two columns. The visit timeline leads the left
  // column directly under allergies/chronic — the at-a-glance flags and known
  // conditions, then the longitudinal record. Narrow (master-detail pane) +
  // mobile: single column.
  if (!narrow) {
    return (
      <div className="flex flex-col gap-[18px]">
        {flagsRow}
        <div className="grid items-start gap-[18px] lg:grid-cols-[1.5fr_1fr]">
          <div className="flex flex-col gap-[18px]">
            {/* Allergies + chronic diseases sit side-by-side — the two highest-
                signal, lowest-volume sections, scannable at a glance. */}
            <div className="grid items-start gap-[18px] sm:grid-cols-2">
              {allergiesCard}
              {chronicCard}
            </div>
            {timeline}
            {labsCard}
            {medsCard}
            {pastCard}
          </div>
          <div className="flex flex-col gap-[18px]">
            {obstetricCard}
            {familyCard}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3.5')}>
      {flagsRow}
      {/* Allergies + chronic diseases side-by-side, then the timeline — same
          reading order as the wide layout, just one stack. */}
      <div className="grid items-start gap-3.5 sm:grid-cols-2">
        {allergiesCard}
        {chronicCard}
      </div>
      {timeline}
      {labsCard}
      {medsCard}
      {obstetricCard}
      {pastCard}
      {familyCard}
    </div>
  );
}

// ---------- Obstetric snapshot ----------

function ObstetricSnapshotCard({
  lmpIso,
  gravidaPara,
}: {
  lmpIso: string;
  gravidaPara?: string;
}) {
  const { t } = useTranslation();
  const poa = poaFromLmp(lmpIso);
  const edd = eddFromLmp(lmpIso);
  const tri = trimester(poa.weeks);
  const poaPct = Math.min(100, (poa.totalDays / GESTATION_DAYS) * 100);
  const poaLabel = t('patients.obWeeksDays', { weeks: poa.weeks, days: poa.days });

  return (
    <SummaryCard titleKey="patients.cardObstetric" icon="calendar">
      <div className="p-4">
        <div className="grid grid-cols-2 gap-2.5">
          {/* Lead stat is Gravida/Para when parity is recorded; otherwise POA
              (which also appears, always, in the progress-bar caption below). */}
          {gravidaPara ? (
            <Stat label={t('patients.obGravidaPara')} value={gravidaPara} />
          ) : (
            <Stat label={t('patients.obPoaLabel')} value={poaLabel} />
          )}
          <Stat label={t('patients.obTrimester')} value={t('patients.obTrimesterValue', { n: tri })} />
          <Stat label={t('patients.obLmp')} value={lmpIso} />
          <Stat label={t('patients.obEdd')} value={edd} />
        </div>
        <div className="relative mt-3.5 h-2 overflow-hidden rounded-full bg-paper-3">
          <div className="absolute inset-y-0 left-0 bg-success/50" style={{ width: `${poaPct}%` }} />
          <div className="absolute top-[-2px] h-3 w-0.5 bg-ink" style={{ left: `${poaPct}%` }} />
        </div>
        <div className="mt-1.5 flex justify-between text-[11px] text-muted">
          <span>{t('patients.obLmp')}</span>
          <span className="font-semibold text-ink-2">{t('patients.obPoa', { poa: poaLabel })}</span>
          <span>{t('patients.obFullTerm')}</span>
        </div>
      </div>
    </SummaryCard>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.06em] text-muted">{label}</div>
      <div className="mt-0.5 text-[14.5px] font-bold tracking-[-0.01em]">{value}</div>
    </div>
  );
}

// ---------- helpers ----------

/**
 * The patient's CURRENT-pregnancy LMP: the latest valid LMP recorded on a
 * consultation's gynae snapshot, excluding any pregnancy that is over. `lmpIso`
 * is free-form consultation JSON, so a malformed/non-existent date (e.g.
 * `2024-02-30`) is filtered out — feeding it to the gestogram math would throw
 * `RangeError` and blank the whole panel.
 *
 * A pregnancy stops being "current" when either (a) the server has retired it
 * into `obstetric.concludedLmps` (the canonical signal), or (b) it is past the
 * conclusion cutoff but the server hasn't reconciled it yet (belt-and-suspenders
 * for a cached history) — `isConcludable` covers that window.
 */
function findLmp(consults: Consultation[], history: ClinicalHistory | undefined): string | null {
  const concluded = new Set(history?.payload.obstetric?.concludedLmps ?? []);
  const current = consults
    .map((c) => ({ lmp: c.payload.gynae?.lmpIso, edd: c.payload.obstetric?.eddIso }))
    .filter((c): c is { lmp: string; edd: string | undefined } => isValidIsoDate(c.lmp))
    .filter((c) => !concluded.has(c.lmp) && !isConcludable(c.lmp, c.edd))
    .map((c) => c.lmp);
  if (current.length === 0) return null;
  // Most recent LMP wins (sort ascending as ISO date strings, take last).
  return current.sort().at(-1) ?? null;
}

function yearOf(iso: string): string {
  return iso.slice(0, 4);
}

/**
 * Secondary meta line for a chronic-disease row: "Since <year>". Dropped when
 * the onset date is absent, so a bare condition renders no line at all.
 */
function chronicMeta(c: ChronicDisease, t: TFunction): string {
  const parts: string[] = [];
  if (c.sinceIso) parts.push(t('patients.sinceYear', { year: yearOf(c.sinceIso) }));
  return parts.join(' · ');
}

/**
 * "G<gravida> P<para>" from the obstetric parity record, or `undefined` when
 * neither count is recorded. `parity` is free-form JSON (`Record<string,
 * number>`), so each field is type-guarded before use — mirrors the formatting
 * on the consultation review stage.
 */
function formatGravidaPara(parity?: Record<string, number>): string | undefined {
  const gravida = typeof parity?.gravida === 'number' ? parity.gravida : undefined;
  const para = typeof parity?.para === 'number' ? parity.para : undefined;
  if (gravida == null && para == null) return undefined;
  return `G${gravida ?? 0} P${para ?? 0}`;
}
